import React, { useEffect, useMemo, useState } from "react";
import "../styles/tailwind.css";
import "../styles/custom.css";
import { useChatState } from "../hooks/useChatState.js";
import ChatWindow from "./ChatWindow";
import ToggleButton from "./ToggleButton";
import { isDomainAllowed } from "../lib/utils.js";
import { fetchAgentConfig } from "../utils/api.js";

const ChatWidget = ({
  // Required props for dynamic config
  orgId,
  apiBaseUrl,
  // Optional override props (will override fetched config if provided)
  darkMode,
  primaryColor,
  position,
  companyName,
  conciergeName,
  companyLogo,
  welcomeMessage,
  quickQuestions,
  quickQuestionsEnabled,
  quickQuestionsLayout = "vertical",
  textColor,
  agentMessageBubbleColor,
  userMessageBoxColor,
  assistantTextColor,
  userTextColor,
  fontFamily,
  fontSize,
  defaultLanguage,
  apiKey,
  agentMessageBubbleFontFamily,
  agentMessageBubbleFontSize,
  userMessageBubbleFontFamily,
  userMessageBubbleFontSize,
  autoOpen = false,
  openDelay = 0,
  locale,
  className = "",
  headerTextBold,
  headerTextItalic,
  enableVoiceInteraction,
  disclaimerText,
  disclaimerPosition,
  showPoweredBy,
  onboardingQuestions,
  collectionPrompt,
  allowedDomains,
  onboardingEnabled,
}) => {
  const [agentConfig, setAgentConfig] = useState(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState(null);

  // Fetch agent configuration on mount
  useEffect(() => {
    const loadAgentConfig = async () => {
      if (!orgId || !apiBaseUrl) {
        setConfigError("orgId and apiBaseUrl are required");
        setIsLoadingConfig(false);
        return;
      }

      try {
        setIsLoadingConfig(true);
        const config = await fetchAgentConfig(apiBaseUrl, orgId);
        setAgentConfig(config);
        setConfigError(null);
      } catch (error) {
        console.error("Failed to load agent configuration:", error);
        setConfigError(error.message || "Failed to load configuration");
      } finally {
        setIsLoadingConfig(false);
      }
    };

    loadAgentConfig();
  }, [orgId, apiBaseUrl]);

  // Map API response to widget props (use props as overrides if provided)
  const widgetConfig = useMemo(() => {
    if (!agentConfig) {
      return null;
    }

    const finalAllowedDomains = allowedDomains ?? agentConfig.allowedDomains ?? [];

    return {
      darkMode: darkMode ?? false,
      primaryColor: primaryColor ?? agentConfig.brandColor ?? "#2563eb",
      companyName: companyName ?? agentConfig.companyName ?? "Support Team",
      conciergeName: conciergeName ?? agentConfig.conciergeName,
      companyLogo: companyLogo ?? agentConfig.avatar ?? null,
      welcomeMessage: welcomeMessage ?? agentConfig.welcomeMessage ?? "Hi! How can we help?",
      quickQuestions: quickQuestions ?? agentConfig.quickQuestions ?? [],
      quickQuestionsEnabled: quickQuestionsEnabled ?? agentConfig.quickQuestionsEnabled,
      textColor: textColor ?? agentConfig.textColor ?? "#000000",
      agentMessageBubbleColor: agentMessageBubbleColor ?? agentConfig.agentMessageBubbleColor,
      userMessageBoxColor: userMessageBoxColor ?? agentConfig.userMessageBoxColor,
      assistantTextColor: assistantTextColor ?? agentConfig.assistantTextColor ?? agentConfig.textColor ?? "#000000",
      userTextColor: userTextColor ?? agentConfig.userTextColor ?? agentConfig.textColor ?? "#000000",
      fontFamily: fontFamily ?? agentConfig.fontFamily ?? agentConfig.agentMessageBubbleFontFamily ?? "Inter",
      fontSize: fontSize ?? agentConfig.fontSize ?? agentConfig.agentMessageBubbleFontSize ?? "14px",
      defaultLanguage: defaultLanguage ?? agentConfig.defaultLanguage ?? "en",
      organizationId: orgId,
      headerTextBold: headerTextBold ?? agentConfig.headerTextBold ?? false,
      headerTextItalic: headerTextItalic ?? agentConfig.headerTextItalic ?? false,
      enableVoiceInteraction: enableVoiceInteraction ?? agentConfig.enableVoiceInteraction ?? false,
      disclaimerText: disclaimerText ?? agentConfig.disclaimerText,
      disclaimerPosition: disclaimerPosition ?? agentConfig.disclaimerPosition ?? "top",
      showPoweredBy: showPoweredBy ?? agentConfig.showPoweredBy ?? true,
      onboardingQuestions: onboardingQuestions ?? agentConfig.onboardingQuestions ?? [],
      collectionPrompt: collectionPrompt ?? agentConfig.collapsiblePrompt,
      allowedDomains: finalAllowedDomains,
      launcherPosition: position ?? agentConfig.launcherPosition ?? "bottom-right",
      onboardingEnabled: onboardingEnabled ?? agentConfig.onboardingEnabled ?? false,
      agentMessageBubbleFontFamily: agentMessageBubbleFontFamily ?? agentConfig.agentMessageBubbleFontFamily ?? fontFamily ?? agentConfig.fontFamily ?? "Inter",
      agentMessageBubbleFontSize: agentMessageBubbleFontSize ?? agentConfig.agentMessageBubbleFontSize ?? fontSize ?? agentConfig.fontSize ?? "14px",
      userMessageBubbleFontFamily: userMessageBubbleFontFamily ?? agentConfig.userMessageBubbleFontFamily ?? fontFamily ?? agentConfig.fontFamily ?? "Inter",
      userMessageBubbleFontSize: userMessageBubbleFontSize ?? agentConfig.userMessageBubbleFontSize ?? fontSize ?? agentConfig.fontSize ?? "14px",
    };
  }, [
    agentConfig,
    darkMode,
    primaryColor,
    position,
    companyName,
    conciergeName,
    companyLogo,
    welcomeMessage,
    quickQuestions,
    quickQuestionsEnabled,
    textColor,
    agentMessageBubbleColor,
    userMessageBoxColor,
    assistantTextColor,
    userTextColor,
    fontFamily,
    fontSize,
    defaultLanguage,
    orgId,
    headerTextBold,
    headerTextItalic,
    enableVoiceInteraction,
    disclaimerText,
    disclaimerPosition,
    showPoweredBy,
    onboardingQuestions,
    onboardingEnabled,
    collectionPrompt,
    allowedDomains,
    agentMessageBubbleFontFamily,
    agentMessageBubbleFontSize,
    userMessageBubbleFontFamily,
    userMessageBubbleFontSize,
  ]);

  // Check if current domain is allowed
  const isAllowed = useMemo(() => {
    if (!widgetConfig) {
      return true;
    }
    return isDomainAllowed(widgetConfig.allowedDomains);
  }, [widgetConfig]);

  // Create default config for useChatState when config is not ready
  const defaultConfig = useMemo(() => ({
    welcomeMessage: "Hi! How can we help?",
    quickQuestions: [],
    apiBaseUrl: apiBaseUrl || "",
    apiKey: apiKey || "",
    organizationId: orgId || "",
    autoOpen: false,
    openDelay: 0,
    darkMode: false,
    enableVoiceInteraction: false,
    onboardingQuestions: [],
    onboardingEnabled: false,
    collectionPrompt: undefined,
  }), [apiBaseUrl, apiKey, orgId]);

  // ALWAYS call hooks before any early returns (Rules of Hooks)
  const {
    isOpen,
    isMinimized,
    isDark,
    messages,
    inputValue,
    isLoading,
    widgetRef,
    setInputValue,
    handleSend,
    handleQuickQuestion,
    sendMessage,
    toggleChat,
    toggleTheme,
    toggleMinimize,
    closeChat,
    handleDecision,
    isVoiceSessionActive,
    voiceStatus,
    voiceError,
    handleVoiceToggle,
    sendBidiTextMessage,
    onboardingActive,
    onboardingCompleted,
    handleOnboardingFormSubmit,
  } = useChatState(
    widgetConfig
      ? {
        welcomeMessage: widgetConfig.welcomeMessage,
        quickQuestions: widgetConfig.quickQuestions,
        apiBaseUrl,
        apiKey,
        organizationId: widgetConfig.organizationId,
        autoOpen,
        openDelay,
        darkMode: widgetConfig.darkMode,
        enableVoiceInteraction: widgetConfig.enableVoiceInteraction,
        onboardingQuestions: widgetConfig.onboardingQuestions,
        onboardingEnabled: widgetConfig.onboardingEnabled,
        collectionPrompt: widgetConfig.collectionPrompt,
      }
      : defaultConfig
  );

  // Set CSS variables for theming (must be called before early returns)
  useEffect(() => {
    if (widgetRef.current && widgetConfig) {
      widgetRef.current.style.setProperty("--cw-primary", widgetConfig.primaryColor);
      if (widgetConfig.fontFamily) {
        widgetRef.current.style.setProperty("--cw-font-family", widgetConfig.fontFamily);
      }
      if (widgetConfig.textColor) {
        widgetRef.current.style.setProperty("--cw-text-color", widgetConfig.textColor);
      }
    }
  }, [widgetConfig, widgetRef]);

  // Don't render widget if domain is not allowed or config is loading/error
  if (isLoadingConfig) {
    return null; // Or return a loading spinner if desired
  }

  if (configError || !widgetConfig) {
    console.error("ChatWidget configuration error:", configError);
    return null;
  }

  if (!isAllowed) {
    return null;
  }

  const positionClasses = {
    "bottom-right": "bottom-5 right-5",
    "bottom-left": "bottom-5 left-5",
    "top-right": "top-5 right-5",
    "top-left": "top-5 left-5",
  };

  return (
    <div
      ref={widgetRef}
      className={`chat-widget-root fixed flex flex-col items-end z-[999999] ${isDark ? "dark" : ""
        } ${className} ${positionClasses[widgetConfig.launcherPosition] || positionClasses["bottom-right"]
        }`}
    >
      <ChatWindow
        isOpen={isOpen}
        isMinimized={isMinimized}
        isDark={isDark}
        primaryColor={widgetConfig.primaryColor}
        textColor={widgetConfig.textColor}
        companyName={widgetConfig.companyName}
        conciergeName={widgetConfig.conciergeName}
        companyLogo={widgetConfig.companyLogo}
        messages={messages}
        isLoading={isLoading}
        inputValue={inputValue}
        setInputValue={setInputValue}
        onSend={handleSend}
        onDirectSend={sendMessage}
        onToggleTheme={toggleTheme}
        onMinimize={toggleMinimize}
        onClose={closeChat}
        quickQuestions={widgetConfig.quickQuestions}
        quickQuestionsEnabled={widgetConfig.quickQuestionsEnabled}
        onQuickQuestion={handleQuickQuestion}
        quickQuestionsLayout={quickQuestionsLayout}
        agentMessageBubbleColor={widgetConfig.agentMessageBubbleColor}
        userMessageBoxColor={widgetConfig.userMessageBoxColor}
        assistantTextColor={widgetConfig.assistantTextColor}
        userTextColor={widgetConfig.userTextColor}
        fontFamily={widgetConfig.fontFamily}
        agentMessageBubbleFontFamily={widgetConfig.agentMessageBubbleFontFamily}
        agentMessageBubbleFontSize={widgetConfig.agentMessageBubbleFontSize}
        userMessageBubbleFontFamily={widgetConfig.userMessageBubbleFontFamily}
        userMessageBubbleFontSize={widgetConfig.userMessageBubbleFontSize}
        fontSize={widgetConfig.fontSize}
        defaultLanguage={widgetConfig.defaultLanguage || locale}
        onDecision={handleDecision}
        headerTextBold={widgetConfig.headerTextBold}
        headerTextItalic={widgetConfig.headerTextItalic}
        enableVoiceInteraction={widgetConfig.enableVoiceInteraction}
        isVoiceSessionActive={isVoiceSessionActive}
        voiceStatus={voiceStatus}
        voiceError={voiceError}
        onVoiceToggle={handleVoiceToggle}
        onBidiSubmit={() => sendBidiTextMessage(inputValue)}
        disclaimerText={widgetConfig.disclaimerText}
        disclaimerPosition={widgetConfig.disclaimerPosition}
        showPoweredBy={widgetConfig.showPoweredBy}
        apiBaseUrl={apiBaseUrl}
        apiKey={apiKey}
        organizationId={widgetConfig.organizationId}
        onboardingQuestions={widgetConfig.onboardingQuestions}
        onboardingActive={onboardingActive}
        onboardingCompleted={onboardingCompleted}
        onOnboardingFormSubmit={handleOnboardingFormSubmit}
        collectionPrompt={widgetConfig.collectionPrompt}
      />

      {!isOpen && (
        <ToggleButton
          isOpen={isOpen}
          isDark={isDark}
          primaryColor={widgetConfig.primaryColor}
          onToggle={toggleChat}
          textColor={widgetConfig.textColor}
        />
      )}
    </div>
  );
};

export default ChatWidget;
